import React, { useMemo } from 'react';
import { useTheme } from '@components/theme';
import { useMaxBreakpoint } from '@hooks/useMediaQuery/useMediaQuery';
import classNames from 'classnames';
import breakpoints from '@constants/breakpoints';
import { IStyleQuizQuestionWithNext } from '@components/shared/questions/utils/withNextQuestion';
import QUESTION_IDS from '@components/shared/questions/utils/questionIds';
import RoundedOptions, { ISelectableOption } from '@components/pages/StyleQuiz/shared/components/Options/RoundedOptions/RoundedOptions';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import TrackingEvents from '@constants/tracking/events';
import {
  IStyleQuizAnswerOption
} from '@graphql/StyleQuiz/fragments/style-quiz-question.graphql';
import styles from './RoomSelection.module.scss';
import NextButton from '../../shared/components/NextButton/NextButton';
import { IAnswer, useQuizStorage } from '../../shared/context/QuizStorageProvider';
import StickyButtonFooter from '../../shared/components/StickyButtonFooter/StickyButtonFooter';

interface IProps {
  question: IStyleQuizQuestionWithNext;
  showMultiSelectCounters: boolean;
}

export default function RoomSelection({
  question,
  showMultiSelectCounters
}: IProps) {
  const {
    title, subtitle, answerOptions, nextUri
  } = question;
  const { setQuestionAnswer, quizAnswers }: any = useQuizStorage();
  const questionAnswers:IAnswer[] = quizAnswers[QUESTION_IDS.ROOM_SELECTION] ?? [];
  const theme = useTheme();
  const isMobile = useMaxBreakpoint('desktop');

  const roomCounter = useMemo(() => questionAnswers.reduce(
    (counter, questionAnswer) => counter + (questionAnswer.counter || 0),
    0
  ), [questionAnswers]);

  const handleGoToNext = () => {
    trackRoomSelection(quizAnswers[QUESTION_IDS.ROOM_SELECTION], answerOptions);
  };

  const handleOptionSelect = (selectedOption: ISelectableOption) => {
    const questionAnswerIndex = questionAnswers.findIndex(
      (qa) => qa.optionId === selectedOption.id
    );
    const questionAnswer = questionAnswerIndex >= 0 ? questionAnswers[questionAnswerIndex] : {
      questionId: QUESTION_IDS.ROOM_SELECTION,
      optionId: selectedOption.id,
      counter: 0
    };

    questionAnswer.counter = questionAnswer.counter! + 1;

    if (questionAnswerIndex >= 0) {
      setQuestionAnswer(QUESTION_IDS.ROOM_SELECTION, [
        ...questionAnswers.slice(0, questionAnswerIndex),
        questionAnswer,
        ...questionAnswers.slice(questionAnswerIndex + 1, questionAnswers.length)
      ]);
    } else {
      setQuestionAnswer(QUESTION_IDS.ROOM_SELECTION, [...questionAnswers, questionAnswer]);
    }
  };

  const handleOptionUnselect = (selectedOption: ISelectableOption, counter: number = -1) => {
    const questionAnswer = questionAnswers.find((qa) => qa.optionId === selectedOption.id);

    if (!questionAnswer) return;

    if (counter === -1) {
      questionAnswer!.counter = questionAnswer.counter! - 1;
    } else {
      questionAnswer.counter = counter;
    }

    if (questionAnswer!.counter === 0) {
      const newAnswers = questionAnswers.filter(
        (answer: IAnswer) => answer.optionId !== selectedOption.id
      );

      setQuestionAnswer(QUESTION_IDS.ROOM_SELECTION, newAnswers);
    } else {
      setQuestionAnswer(QUESTION_IDS.ROOM_SELECTION, [...questionAnswers]);
    }
  };

  const nextPageLink = questionAnswers.length > 1
    ? '/interior-design-style-quiz/room-focus'
    : nextUri;
  const nextButton = (
    <NextButton
      isDisabled={!questionAnswers?.length}
      link={nextPageLink!}
      onClick={handleGoToNext}
    />
  );

  return (
    <div className={styles.Root}>
      <div className={styles.Wrapper}>
        <div className={styles.Container}>
          <div className={styles.Text}>
            <p className={classNames(
              styles.Title,
              {
                [theme.styles.Header]: isMobile,
                [theme.styles.Title1]: !isMobile,
              }
            )}
            >
              {title}
            </p>
            <p className={classNames(styles.Subtitle, theme.styles.Body)}>{subtitle}</p>
          </div>
        </div>
        <RoundedOptions
          initialAnswers={questionAnswers}
          className={styles.Options}
          answerOptions={answerOptions}
          multiSelect
          multiSelectCounters={showMultiSelectCounters}
          onOptionSelect={handleOptionSelect}
          onOptionUnselect={handleOptionUnselect}
          justify={'center'}
          breakpoints={{
            [breakpoints.desktop]: {
              justify: 'left',
            }
          }}
        />
        <StickyButtonFooter>
          {showMultiSelectCounters && (
            <span className={styles.RoomsCounter}>TOTAL ROOMS: { roomCounter }</span>
          )}
          {nextButton}
        </StickyButtonFooter>
      </div>
    </div>
  );
}

function trackRoomSelection(
 quizAnswers: IAnswer[],
 answerOptions: IStyleQuizAnswerOption[],
) {
  analyticsTrack(TrackingEvents.ONBOARDING_ROOMS_SELECTED,
    quizAnswers.map((qa) => {
      const answer = answerOptions.find((ao) => qa.optionId === ao.id);

      if (!answer) return null;

      return {
        room_type: answer.title,
        room_quantity: qa.counter,
      };
    }));
  }
